import type { FulfillmentMethod } from '../types/store-fulfillment';
import { getOrderStatusTranslationKey } from '../lib/store-fulfillment';
/**
 * F-018: Reusable order status badge.
 *
 * Renders a colored pill with a translated status label. Used by F-018
 * (order confirmation), F-019 (order history), F-020 (polling), F-022
 * (admin detail).
 *
 * Color mapping per DESIGN.md:
 *   ordered   -> Honey Warning #C48B14 (15% bg opacity)
 *   shipped   -> Za'atar Olive #7A8B4A (15% bg opacity)
 *   delivered -> Sage Success #5A8C4A (15% bg opacity)
 *   cancelled -> Terracotta Error #B8472F (15% bg opacity)
 *   rejected  -> Terracotta Error #B8472F (15% bg opacity)
 *
 * Badge spec: 4px radius, 2px 8px padding, Inter 500 11px uppercase.
 */
import { useTranslation } from '../i18n';
import type { OrderStatus } from '../types/order';

export interface OrderStatusBadgeProps {
  readonly status: OrderStatus;
  readonly method?: FulfillmentMethod;
  readonly variant?: 'legacy' | 'storefrontNeutral';
}

const STATUS_STYLES: Record<OrderStatus, string> = {
  ordered: 'bg-[#C48B14]/15 text-[#C48B14] dark:bg-[#C48B14]/25 dark:text-[#D9A84E]',
  shipped: 'bg-[#7A8B4A]/15 text-[#7A8B4A] dark:bg-[#7A8B4A]/25 dark:text-[#9FB06A]',
  delivered: 'bg-[#5A8C4A]/15 text-[#5A8C4A] dark:bg-[#5A8C4A]/25 dark:text-[#7DB06A]',
  cancelled: 'bg-[#B8472F]/15 text-[#B8472F] dark:bg-[#B8472F]/25 dark:text-[#D66A54]',
  rejected: 'bg-[#B8472F]/15 text-[#B8472F] dark:bg-[#B8472F]/25 dark:text-[#D66A54]',
};

const STOREFRONT_NEUTRAL_STATUS_STYLES: Record<OrderStatus, string> = {
  ordered: 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  shipped: 'bg-teal-50 text-teal-700 dark:bg-teal-500/15 dark:text-teal-300',
  delivered: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  cancelled: 'bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300',
  rejected: 'bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300',
};

export function OrderStatusBadge({
  status,
  variant = 'legacy',
  method = 'delivery',
}: OrderStatusBadgeProps): React.JSX.Element {
  const { t } = useTranslation();
  const label = t(getOrderStatusTranslationKey(status, method));
  const statusClass =
    variant === 'storefrontNeutral'
      ? STOREFRONT_NEUTRAL_STATUS_STYLES[status]
      : STATUS_STYLES[status];

  return (
    <span
      data-testid="order-status-badge"
      data-status={status}
      className={`inline-block rounded-[4px] px-2 py-0.5 font-sans text-[11px] font-medium uppercase tracking-wider ${statusClass}`}
    >
      {label}
    </span>
  );
}
